import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { loginWithPasskey, loginWithPassword, passkeysSupported } from "../../api/auth";
import { errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { nl } from "../../i18n/nl";
import { AuthLayout, Field, FormError } from "./AuthLayout";
import styles from "./auth.module.css";
import { NEXT_PARAM, safeNext } from "./RequireAuth";
import { useRefreshSession } from "./session";

const t = nl.auth;

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useRefreshSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<{ email?: boolean; password?: boolean }>({});
  const supportsPasskeys = passkeysSupported();

  async function done() {
    await refresh();
    void navigate(safeNext(params.get(NEXT_PARAM)), { replace: true });
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await done();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const empty = { email: !email.trim(), password: !password };
    setMissing(empty);
    if (empty.email || empty.password) return;
    void run(() => loginWithPassword(email, password));
  }

  return (
    <AuthLayout>
      <h1 className={styles.title}>{t.login.title}</h1>
      <p className={styles.lead}>{t.heroLead}</p>

      {supportsPasskeys && (
        <>
          <Button
            variant="primary"
            size="large"
            className={styles.full}
            style={{ marginTop: 22 }}
            disabled={busy}
            onClick={() => {
              void run(loginWithPasskey);
            }}
          >
            {t.login.passkey}
          </Button>
          <p className={styles.help}>{t.login.passkeyHelp}</p>
          <p className={styles.divider}>{t.login.or}</p>
        </>
      )}

      <form className={styles.form} onSubmit={submit} noValidate>
        <FormError message={error} />
        <Field
          label={t.email}
          type="email"
          inputMode="email"
          autoComplete="username webauthn"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t.emailPlaceholder}
          value={email}
          error={missing.email ? t.required : null}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <Field
          label={t.password}
          type="password"
          autoComplete="current-password"
          value={password}
          error={missing.password ? t.required : null}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
        <Button
          type="submit"
          variant={supportsPasskeys ? "secondary" : "primary"}
          size="large"
          className={styles.full}
          disabled={busy}
        >
          {busy ? t.working : t.login.submit}
        </Button>
      </form>

      <div className={styles.links}>
        <Link to="/herstel">{t.login.lost}</Link>
        <Link to="/registreren">
          {t.login.noAccount} {t.login.register}
        </Link>
      </div>
    </AuthLayout>
  );
}
